import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  List,
  Divider,
  ListItem,
  Grid,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';

import axios from 'axios';
import Navbar from './Navbar';
import Breadcrumb from './BreadCrump';
import AssignmentSubmissionModal from './AssignmentSubmissionModal';

function ChapterDetailsPage() {
  const { courseId, chapterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  const token = localStorage.getItem('token');

  const [chapter, setChapter] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editedName, setEditedName] = useState('');

  /* ========= VIDEO MODAL ========= */
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const handleOpenVideo = (doc) => {
    setActiveVideo(`http://196.216.9.34:5020${doc.path}`);
    setOpenVideoModal(true);
  };
  /* =============================== */

  useEffect(() => {
    fetchChapter();
    fetchDocuments();
    fetchAssignments();
  }, [courseId, chapterId]);

  const fetchChapter = async () => {
    try {
      const res = await fetch(
        `http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setChapter(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await fetch(
        `http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setDocuments(Array.isArray(data.documents) ? data.documents : []);
    } catch {
      setDocuments([]);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await fetch(
        `http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}/assignments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setAssignments(data.assignments || []);
    } catch {}
  };

  /* ========= FIXED DELETE FUNCTION ========= */
  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const res = await fetch(
        `http://196.216.9.34:5021/api/documents/${documentId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Failed to delete document');
      }

      // ✅ Remove from UI instantly
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));

    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete document. Please try again.');
    }
  };
  /* ======================================= */

  const handleSaveEdit = async () => {
    await fetch(
      `http://196.216.9.34:5021/api/documents/${editingDoc.id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: editedName })
      }
    );
    setEditModalOpen(false);
    fetchDocuments();
  };

  const getIconForType = (type = '') => {
    if (type.includes('pdf')) return <PictureAsPdfIcon />;
    if (type.includes('image')) return <ImageIcon />;
    if (type.includes('audio')) return <AudiotrackIcon />;
    if (type.includes('video')) return <OndemandVideoIcon />;
    if (type === 'link') return <LinkIcon />;
    return <DescriptionIcon />;
  };

  if (loading) return <Box p={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ minHeight: '100vh', py: 6 }}>
      <Container maxWidth={false} sx={{ mt: 5 }}>
        <Navbar />
        <Breadcrumb currentPage="Chapter Details" />

        <Paper sx={{ p: 4, mt: 6 }}>
          <Typography variant="h6" textAlign="center" mb={2}>
            Uploaded Documents
          </Typography>

          <List>
            {documents.map((doc) => (
              <ListItem
                key={doc.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1,
                  borderRadius: 2,
                  background: '#f9f9f9',
                  '&:hover': { background: '#e3f2fd' }
                }}
              >
                <Box
                  onClick={() =>
                    doc.type?.includes('video')
                      ? handleOpenVideo(doc)
                      : window.open(
                          doc.is_link
                            ? doc.path
                            : `http://196.216.9.34:5020${doc.path}`,
                          '_blank',
                          'noopener'
                        )
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    flexGrow: 1
                  }}
                >
                  <Box sx={{ mr: 2 }}>{getIconForType(doc.type)}</Box>
                  <Typography noWrap>{doc.name}</Typography>
                </Box>

                {user?.role === 'instructor' && (
                  <Box>
                    <IconButton onClick={() => {
                      setEditingDoc(doc);
                      setEditedName(doc.name);
                      setEditModalOpen(true);
                    }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(doc.id)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* VIDEO MODAL */}
        <Modal
          open={openVideoModal}
          onClose={() => {
            setOpenVideoModal(false);
            setActiveVideo(null);
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', md: '70%' },
              bgcolor: 'black',
              borderRadius: 2,
              p: 2
            }}
          >
            <Box display="flex" justifyContent="flex-end">
              <IconButton sx={{ color: 'white' }} onClick={() => setOpenVideoModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {activeVideo && (
              <video
                src={activeVideo}
                controls
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                style={{ width: '100%', maxHeight: '70vh', borderRadius: 8 }}
              />
            )}
          </Box>
        </Modal>

        {/* EDIT MODAL */}
        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 2
            }}
          >    

            <Typography mb={2}>Edit Document Name</Typography>
            <TextField
              fullWidth
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
            />
            <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
            </Box>
          </Box>
        </Modal>

      </Container>
    </Box>
  );
}

export default ChapterDetailsPage;

// import React, { useEffect, useState } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import {
//   Typography,
//   Box,
//   CircularProgress,
//   Alert,
//   Container,
//   Paper,
//   Button,
//   TextField,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   LinearProgress,
//   Link,
//   List,
//   Divider,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   Grid,
//   Card,
//   Modal
// } from '@mui/material';
// import Stack from '@mui/material/Stack';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   IconButton
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import EditIcon from '@mui/icons-material/Edit';
// import DeleteIcon from '@mui/icons-material/Delete';
// import AssignmentSubmissionModal from './AssignmentSubmissionModal';



// import { useLocation } from 'react-router-dom';

// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import CloudUploadIcon from '@mui/icons-material/CloudUpload';
// import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
// import ImageIcon from '@mui/icons-material/Image';
// import AudiotrackIcon from '@mui/icons-material/Audiotrack';
// import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
// import LinkIcon from '@mui/icons-material/Link';
// import DescriptionIcon from '@mui/icons-material/Description';
// import axios from 'axios';
// import Navbar from './Navbar';
// import LeftNavbar from './LeftNav';
// import Breadcrumb from './BreadCrump';

// function ChapterDetailsPage() {
//   const { courseId, chapterId } = useParams();
//   const [chapter, setChapter] = useState(null);
//   const [documents, setDocuments] = useState([]); // Initialize as array
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [uploadMode, setUploadMode] = useState('file');
//   const [fileInputs, setFileInputs] = useState([]);
//   const [linkInputs, setLinkInputs] = useState(['']);
//   const [uploading, setUploading] = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(0);
//   const [uploadStatus, setUploadStatus] = useState('');
//   const token = localStorage.getItem('token');
//   const navigate = useNavigate();
//   const location = useLocation();
//   const user = location.state?.user;
//   const [viewStats, setViewStats] = useState(null);
//   const [assignments, setAssignments] = useState([]);
//   const [newAssignment, setNewAssignment] = useState({
//     title: '',
//     description: '',
//     dueDate: '',
//   });
//   const [openUploadModal, setOpenUploadModal] = useState(false);
//   const [openAssignmentModal, setOpenAssignmentModal] = useState(false);
//   const [editModalOpen, setEditModalOpen] = useState(false);
//   const [editingDoc, setEditingDoc] = useState(null);
//   const [editedName, setEditedName] = useState('');

//   const [chapterEditModalOpen, setChapterEditModalOpen] = useState(false);
//   const [editedChapterTitle, setEditedChapterTitle] = useState(chapter?.title || '');
//   const [editedChapterDescription, setEditedChapterDescription] = useState(chapter?.description || '');
//   const [openModalFor, setOpenModalFor] = useState(null); // assignmentId or null

//   const handleEditClick = (doc) => {
//     setEditingDoc(doc);
//     setEditedName(doc.name);
//     setEditModalOpen(true);
//   };

//   const handleSaveChapterEdit = async () => {
//     try {
//       const response = await fetch(`http://196.216.9.34:5021/api/chapters/${chapter.id}`, {
//         method: 'PUT',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           title: editedChapterTitle,
//           description: editedChapterDescription
//         })
//       });
  
//       if (!response.ok) throw new Error('Failed to update chapter.');
  
//       const updated = await response.json();
//       console.log('Chapter updated:', updated);
  
//       // Optional: refresh UI
//       setChapter((prev) => ({
//         ...prev,
//         title: editedChapterTitle,
//         description: editedChapterDescription
//       }));
  
//       setChapterEditModalOpen(false);
//     } catch (error) {
//       console.error(error);
//       // Show user-friendly error toast/snackbar
//     }
//   };
  
//   // Assignment-specific modal state
// const [assignmentEditModalOpen, setAssignmentEditModalOpen] = useState(false);
// const [assignmentDeleteModalOpen, setAssignmentDeleteModalOpen] = useState(false);
// const [selectedAssignment, setSelectedAssignment] = useState(null);
// const [assignmentEditForm, setAssignmentEditForm] = useState({
//   title: '',
//   description: '',
//   due_date: ''
// });

// const handleOpenAssignmentEdit = (assignment) => {
//   setSelectedAssignment(assignment);
//   setAssignmentEditForm({
//     title: assignment.title || '',
//     description: assignment.description || '',
//     due_date: assignment.due_date?.slice(0, 10) || ''
//   });
//   setAssignmentEditModalOpen(true);
// };

// const handleOpenAssignmentDelete = (assignmentId) => {
//   setSelectedAssignment({ id: assignmentId });
//   setAssignmentDeleteModalOpen(true);
// };

// const handleAssignmentEditChange = (e) => {
//   const { name, value } = e.target;
//   setAssignmentEditForm(prev => ({ ...prev, [name]: value }));
// };

// const handleSaveAssignmentEdit = async () => {
//   if (!selectedAssignment || !selectedAssignment.id) return;

//   try {
//     const response = await fetch(`http://196.216.9.34:5021/api/assignments/${selectedAssignment.id}`, {
//       method: 'PUT',
//       headers: {
//         'Content-Type': 'application/json'
//       },
//       body: JSON.stringify({
//         title: assignmentEditForm.title,
//         description: assignmentEditForm.description,
//         dueDate: assignmentEditForm.due_date || assignmentEditForm.dueDate
//       })
//     });

//     const data = await response.json();

//     if (!response.ok) {
//       console.error('Error updating assignment:', data.message || response.statusText);
//       return;
//     }

//     console.log('Assignment updated:', data.message);

//     // Optionally refresh assignment list here
//     await fetchAssignments();

//     setAssignmentEditModalOpen(false);
//   } catch (error) {
//     console.error('Error during assignment update:', error);
//   }
// };


// const handleConfirmAssignmentDelete = async () => {
//   if (!selectedAssignment || !selectedAssignment.id) return;

//   try {
//     const response = await fetch(`http://196.216.9.34:5021/api/assignments/${selectedAssignment.id}`, {
//       method: 'DELETE'
//     });

//     const data = await response.json();

//     if (!response.ok) {
//       console.error('Error deleting assignment:', data.message || response.statusText);
//       return;
//     }

//     console.log('Assignment deleted:', data.message);

//     // Optionally refresh the assignment list
//     await fetchAssignments();

//     setAssignmentDeleteModalOpen(false);
//   } catch (error) {
//     console.error('Error during assignment deletion:', error);
//   }
// };


  
  
//   const handleSaveEdit = async () => {
//     try {
//       console.log("displaying new name");
//       console.log(editedName);
//       const res = await fetch(`http://196.216.9.34:5021/api/documents/${editingDoc.id}`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify({ name: editedName })
//       });
  
//       if (!res.ok) throw new Error('Failed to update document name');
//       setEditModalOpen(false);
//       setEditingDoc(null);
//       setEditedName('');
//       fetchDocuments(); // Refresh documents
//     } catch (err) {
//       console.error(err.message);
//     }
//   };


//   useEffect(() => {
//     fetchChapter();
//     fetchDocuments();
//     fetchAssignments();
//   }, [courseId, chapterId]);

//   const fetchAssignments = async () => {
//     try {
//       const res = await fetch(`http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}/assignments`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(data.message || 'Failed to fetch assignments');
//       setAssignments(data.assignments || []);
//     } catch (err) {
//       console.error('Failed to fetch assignments:', err.message);
//     }
//   };

  

//   const handleCreateAssignment = async () => {
//   const formData = new FormData();
//   formData.append('title', newAssignment.title);
//   formData.append('description', newAssignment.description);
//   formData.append('dueDate', newAssignment.dueDate);

  

//   try {
//     const res = await fetch(`http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}/assignments`, {
//       method: 'POST',
//       headers: {
//         Authorization: `Bearer ${token}`
//         // Do NOT set Content-Type when using FormData — the browser will set it
//       },
//       body: formData,
//     });

//     const data = await res.json();
//     if (!res.ok) throw new Error(data.message || 'Failed to create assignment');

//     setNewAssignment({ title: '', description: '', dueDate: '' });
    
//     fetchAssignments();
//   } catch (err) {
//     console.error(err.message);
//   }
// };

  
  

//   const fetchChapter = async () => {
//     try {
//       const res = await fetch(`http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(data.message || 'Failed to fetch chapter');
//       setChapter(data);
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAssignmentChange = (e) => {
//     const { name, value } = e.target;
//     setNewAssignment(prev => ({ ...prev, [name]: value }));
//   };

//   const handleDelete = async (documentId) => {
//     if (!window.confirm('Are you sure you want to delete this document?')) return;
  
//     try {
//       await axios.delete(`http://196.216.9.34:5021/api/documents/${documentId}`);
//       // Optional: Refresh list or remove the item locally
//       setDocuments(prev => prev.filter(doc => doc.id !== documentId));
//     } catch (error) {
//       console.error('Delete failed:', error);
//       alert('Failed to delete document');
//     }
//   };
  
  
  

//   const fetchDocuments = async () => {
//     try {
//       const res = await fetch(`http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}/documents`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(data.message || 'Failed to fetch documents');
//       // Expecting data.documents to be an array
//       setDocuments(Array.isArray(data.documents) ? data.documents : []); 
//     } catch (err) {
//       console.error('Failed to fetch documents:', err.message);
//       setDocuments([]); // reset to empty on failure
//     }
//   };

//   const handleFileChange = (e, index) => {
//     const newFiles = [...fileInputs];
//     newFiles[index] = e.target.files[0];
//     setFileInputs(newFiles);
//   };

//   const handleLinkChange = (e, index) => {
//     const newLinks = [...linkInputs];
//     newLinks[index] = e.target.value;
//     setLinkInputs(newLinks);
//   };

//   const addNewField = () => {
//     if (uploadMode === 'file') {
//       setFileInputs([...fileInputs, null]);
//     } else {
//       setLinkInputs([...linkInputs, '']);
//     }
//   };

//   const handleUpload = async () => {
//     setUploading(true);
//     setUploadStatus('');
//     setUploadProgress(0);

//     const formData = new FormData();

//     if (uploadMode === 'file') {
//       fileInputs.forEach(file => {
//         if (file) formData.append('files', file);
//       });
//     } else {
//       linkInputs.forEach(link => {
//         if (link.trim()) formData.append('links', link.trim());
//       });
//     }

//     try {
//       await axios.post(
//         `http://196.216.9.34:5020/api/courses/${courseId}/chapters/${chapterId}/documents`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'multipart/form-data'
//           },
//           onUploadProgress: (progressEvent) => {
//             const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
//             setUploadProgress(percent);
//           }
//         }
//       );

//       setUploadStatus('Upload successful!');
//       setFileInputs([]);
//       setLinkInputs(['']);
//       fetchDocuments(); // Refresh document list
//     } catch (err) {
//       console.error(err);
//       setUploadStatus('Upload failed. Please try again.');
//     } finally {
//       setUploading(false);
//     }
//   };

//   const getIconForType = (type = '') => {
//     if (type.includes('pdf')) return <PictureAsPdfIcon />;
//     if (type.includes('image')) return <ImageIcon />;
//     if (type.includes('audio')) return <AudiotrackIcon />;
//     if (type.includes('video')) return <OndemandVideoIcon />;
//     if (type === 'link') return <LinkIcon />;
//     return <DescriptionIcon />;
//   };


//   useEffect(() => {
//     if (user?.role === 'student') {
//       fetch(`http://196.216.9.34:5020/api/chapters/${chapterId}/view`, {
//         method: 'POST',
//         headers: { Authorization: `Bearer ${token}` },
//       }).catch(console.error);
//     }
   
//   }, [chapterId]);
  

//   if (loading) return <Box p={4}><CircularProgress /></Box>;
//   if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
//   if (!chapter) return null;

//   return (
//     <Box
//     sx={{
//       minHeight: '100vh',
//       width: '100%',
//       background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.2))', // Example dashboard background
//       backgroundAttachment: 'fixed',
//       backgroundSize: 'cover',
//       py: 6
//     }}
//   >
    
//       <Container maxWidth={false} sx={{ mt: 5, px: { xs: 2, md: 6 } }}>
//       <Navbar />
//       <Breadcrumb style={{ mt: 55 }} currentPage="Chapter Details" />

//         <Grid container
//           direction="column"
//           alignItems="center"
//           spacing={4}
//           sx={{ mt: 8 }}>
//           {/* Chapter Info */}
//           <Grid container justifyContent="center">
//             <Grid item xs={12} md={8}>
//               <Paper elevation={6}
//         sx={{
//           p: 4,
//           borderRadius: 4,
//           background: 'rgba(255, 255, 255, 0.85)',
//           backdropFilter: 'blur(10px)',
//           boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
//           textAlign: 'center',
//           mb: 5,
//         }}>
//                 <Typography variant="h3" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                   {chapter?.title || 'Chapter Title'}
                  
//                   {user?.role === 'instructor' && (
//                     <IconButton
//                       size="small"
//                       sx={{ ml: 1 }}
//                       onClick={() => setChapterEditModalOpen(true)}
//                     >
//                       <EditIcon fontSize="small" />
//                     </IconButton>
//                   )}
//                 </Typography>
//                 <Divider sx={{ my: 2 }} />

//                 <Typography variant="subtitle1" color="text.secondary">
//                   {chapter?.description || 'Course description not available.'}
//                 </Typography>

//               </Paper>
//             </Grid>
//           </Grid>

//           {/* Upload Section */}
//           {user?.role === 'instructor' && (
//             <Grid item xs={12}>
//             <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
//               <Button
//                 variant="contained"
//                 startIcon={<CloudUploadIcon />}
//                 onClick={() => setOpenUploadModal(true)}
//               >
//                 Upload Documents
//               </Button>
          
//               <Button
//                 variant="contained"
//                 color="secondary"
//                 onClick={() => setOpenAssignmentModal(true)}
//               >
//                 Create Assignment
//               </Button>
//             </Box>
//           </Grid>
          
          
          
//           )}

          

            
//           {/* Uploaded Documents */}
//           {/* Force new row for Uploaded Documents */}
//           <Grid container justifyContent="center" sx={{ mt: 6 }}>
//   <Grid item xs={12} sm={10} md={8} lg={6}>
//     <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
//       <Typography variant="h6" fontWeight="bold" gutterBottom textAlign="center">
//         Uploaded Documents
//       </Typography>
//       {!Array.isArray(documents) || documents.length === 0 ? (
//         <Typography textAlign="center">No documents uploaded yet.</Typography>
//       ) : (
//         <List sx={{ mt: 2, width: '100%' }}>
//           {documents.map((doc, index) => (
//             <ListItem
//               key={index}
//               sx={{
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'space-between',
//                 px: 2,
//                 py: 1.5,
//                 borderRadius: 2,
//                 backgroundColor: '#f9f9f9',
//                 mb: 1,
//                 transition: 'background-color 0.2s',
//                 '&:hover': {
//                   backgroundColor: '#e3f2fd',
//                 }
//               }}
//             >
//               <Box
//                 component="a"
//                 href={doc.is_link ? doc.path : `http://196.216.9.34:5020${doc.path}`}
//                 target="_blank"
//                 rel="noopener"
//                 sx={{
//                   display: 'flex',
//                   alignItems: 'center',
//                   textDecoration: 'none',
//                   color: 'inherit',
//                   flexGrow: 1,
//                   overflow: 'hidden'
//                 }}
//               >
//                 <Box sx={{ mr: 2, color: '#1976d2', fontSize: 28 }}>
//                   {getIconForType(doc.type)}
//                 </Box>
//                 <Typography
//                   variant="body1"
//                   sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
//                 >
//                   {doc.name}
//                 </Typography>
//               </Box>
//               {user?.role === 'instructor' && (
//               <Box>
//                   <IconButton onClick={() => handleEditClick(doc)} edge="end" size="small">
//                     <EditIcon fontSize="small" />
//                   </IconButton>
//                   <IconButton onClick={() => handleDelete(doc.id)} edge="end" size="small" sx={{ ml: 1 }}>
//                     <DeleteIcon fontSize="small" color="error" />
//                   </IconButton>
//               </Box>
              

              
//               )}
//             </ListItem>
//           ))}
//         </List>
//       )}
//     </Paper>
//   </Grid>

//   {/* Edit Modal */}
//   <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
//     <Box
//       sx={{
//         position: 'absolute',
//         top: '50%',
//         left: '50%',
//         transform: 'translate(-50%, -50%)',
//         width: 400,
//         bgcolor: 'background.paper',
//         borderRadius: 2,
//         boxShadow: 24,
//         p: 4,
//       }}
//     >
//       <Typography variant="h6" mb={2}>Edit Document Name</Typography>
//       <TextField
//         fullWidth
//         value={editedName}
//         onChange={(e) => setEditedName(e.target.value)}
//         label="Document Name"
//         variant="outlined"
//         sx={{ mb: 2 }}
//       />
//       <Box display="flex" justifyContent="flex-end" gap={2}>
//         <Button variant="outlined" onClick={() => setEditModalOpen(false)}>Cancel</Button>
//         <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
//       </Box>
//     </Box>
//   </Modal>
// </Grid>





//           <Dialog
//             open={openAssignmentModal}
//             onClose={() => setOpenAssignmentModal(false)}
//             fullWidth
//             maxWidth="sm"
//           >
//             <DialogTitle>
//               Create Assignment
//               <IconButton
//                 aria-label="close"
//                 onClick={() => setOpenAssignmentModal(false)}
//                 sx={{
//                   position: 'absolute',
//                   right: 8,
//                   top: 8,
//                   color: (theme) => theme.palette.grey[500]
//                 }}
//               >
//                 <CloseIcon />
//               </IconButton>
//             </DialogTitle>

//             <DialogContent dividers>
//               <TextField
//                 name="title"
//                 label="Assignment Title"
//                 fullWidth
//                 value={newAssignment.title}
//                 onChange={handleAssignmentChange}
//                 sx={{ mb: 2 }}
//               />

//               <TextField
//                 name="description"
//                 label="Description"
//                 fullWidth
//                 multiline
//                 rows={4}
//                 value={newAssignment.description}
//                 onChange={handleAssignmentChange}
//                 sx={{ mb: 2 }}
//               />

//               <TextField
//                 name="dueDate"
//                 label="Due Date"
//                 type="date"
//                 fullWidth
//                 InputLabelProps={{ shrink: true }}
//                 value={newAssignment.dueDate}
//                 onChange={handleAssignmentChange}
//                 sx={{ mb: 2 }}
//               />
//             </DialogContent>

//             <DialogActions>
//               <Button onClick={() => setOpenAssignmentModal(false)} color="secondary">
//                 Cancel
//               </Button>
//               <Button variant="contained" onClick={handleCreateAssignment}>
//                 Create Assignment
//               </Button>
//             </DialogActions>
//           </Dialog>


          
          
//   {/* List of Assignments */}
//  {/* List of Assignments */}
// {assignments.length > 0 && (
//   <List>
//     <Typography variant="h6" fontWeight="bold" gutterBottom textAlign="center">
//       Assignments
//     </Typography>
//     {assignments.map((assignment, index) => (
//       <ListItem
//       key={index}
//       sx={{
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'space-between',
//         px: 2,
//         py: 1.5,
//         mb: 1,
//         borderRadius: 2,
//         backgroundColor: '#f9f9f9',
//         transition: 'background-color 0.2s',
//         '&:hover': {
//           backgroundColor: '#e3f2fd',
//         },
//       }}
//     >
//       <Box sx={{ flex: 1, overflow: 'hidden' }}>
//         <Typography variant="subtitle1" fontWeight="medium" noWrap>
//           {assignment.title}
//         </Typography>
//         <Typography
//           variant="body2"
//           color="text.secondary"
//           sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
//         >
//           {assignment.description}
//         </Typography>
//       </Box>
//       <Typography
//         variant="caption"
//         color="text.secondary"
//         sx={{ minWidth: 'fit-content', ml: 2, mr: 2 }}
//       >
//         Due: {new Date(assignment.due_date || assignment.dueDate).toLocaleDateString()}
//       </Typography>
//       {user?.role === 'instructor' ? (
//       <Box>
//         <IconButton onClick={() => handleOpenAssignmentEdit(assignment)}>
//           <EditIcon fontSize="small" />
//         </IconButton>
//         <IconButton onClick={() => handleOpenAssignmentDelete(assignment.id)}>
//           <DeleteIcon fontSize="small" color="error" />
//         </IconButton>
//       </Box>
//       ) : (
//         <Button
//         variant="outlined"
//         size="small"
//         onClick={() => setOpenModalFor(assignment.id)}
//       >
//         Submit
//       </Button>
//       )}
//     </ListItem>
    
    
//     ))}
//   </List>
  
// )}  
//  {/* Modal */}
//  {openModalFor && (
//         <AssignmentSubmissionModal
//           open={!!openModalFor}
//           onClose={() => setOpenModalFor(null)}
//           courseId={courseId}
//           chapterId={
//             assignments.find((a) => a.id === openModalFor)?.chapter_id
//           }
//           assignmentId={openModalFor}
//         />
//       )}
          
//         </Grid>
//         <Dialog
//           open={openUploadModal}
//           onClose={() => setOpenUploadModal(false)}
//           fullWidth
//           maxWidth="md"
//         >
//           <DialogTitle>
//             Upload Documents
//             <IconButton
//               aria-label="close"
//               onClick={() => setOpenUploadModal(false)}
//               sx={{
//                 position: 'absolute',
//                 right: 8,
//                 top: 8,
//                 color: (theme) => theme.palette.grey[500]
//               }}
//             >
//               <CloseIcon />
//             </IconButton>
//           </DialogTitle>

//           <DialogContent dividers>
//             <RadioGroup
//               row
//               value={uploadMode}
//               onChange={(e) => setUploadMode(e.target.value)}
//               sx={{ mb: 3 }}
//             >
//               <FormControlLabel value="file" control={<Radio />} label="Upload from Local Disk" />
//               <FormControlLabel value="link" control={<Radio />} label="Add Document Link" />
//             </RadioGroup>

//             {uploadMode === 'file' ? (
//               <Box>
//                 {fileInputs.map((file, index) => (
//                   <Box
//                     key={index}
//                     sx={{
//                       border: '2px dashed #ccc',
//                       borderRadius: 2,
//                       p: 2,
//                       mb: 2,
//                       textAlign: 'center',
//                       backgroundColor: '#fafafa'
//                     }}
//                   >
//                     <input
//                       type="file"
//                       accept="application/pdf,image/*,video/*,audio/*,.ppt,.pptx"
//                       onChange={(e) => handleFileChange(e, index)}
//                     />
//                     {file && <Typography mt={1}>{file.name}</Typography>}
//                   </Box>
//                 ))}
//               </Box>
//             ) : (
//               <Box>
//                 {linkInputs.map((link, index) => (
//                   <TextField
//                     key={index}
//                     label={`Document Link ${index + 1}`}
//                     fullWidth
//                     value={link}
//                     onChange={(e) => handleLinkChange(e, index)}
//                     sx={{ mb: 2 }}
//                   />
//                 ))}
//               </Box>
//             )}

//             <Button
//               variant="outlined"
//               onClick={addNewField}
//               startIcon={<CloudUploadIcon />}
//               sx={{ mt: 2 }}
//             >
//               Add Document
//             </Button>

//             <Button
//               variant="contained"
//               color="primary"
//               onClick={handleUpload}
//               disabled={uploading}
//               sx={{ mt: 2, ml: 2 }}
//             >
//               {uploading ? 'Uploading...' : 'Upload Documents'}
//             </Button>

//             {uploading && (
//               <Box sx={{ mt: 2 }}>
//                 <LinearProgress variant="determinate" value={uploadProgress} />
//                 <Typography variant="body2" mt={1}>
//                   Uploading: {uploadProgress}%
//                 </Typography>
//               </Box>
//             )}

//             {uploadStatus && (
//               <Typography mt={2} color={uploadStatus.includes('failed') ? 'error' : 'success.main'}>
//                 {uploadStatus}
//               </Typography>
//             )}
//           </DialogContent>

//           <DialogActions>
//             <Button onClick={() => setOpenUploadModal(false)} color="secondary">
//               Close
//             </Button>
//           </DialogActions>
//         </Dialog>


//         <Dialog open={assignmentEditModalOpen} onClose={() => setAssignmentEditModalOpen(false)} fullWidth>
//           <DialogTitle>Edit Assignment</DialogTitle>
//           <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//             <TextField
//               label="Title"
//               name="title"
//               value={assignmentEditForm.title}
//               onChange={handleAssignmentEditChange}
//               fullWidth
//             />
//             <TextField
//               label="Description"
//               name="description"
//               value={assignmentEditForm.description}
//               onChange={handleAssignmentEditChange}
//               fullWidth
//               multiline
//               rows={3}
//             />
//             <TextField
//               label="Due Date"
//               name="due_date"
//               type="date"
//               value={assignmentEditForm.due_date}
//               onChange={handleAssignmentEditChange}
//               InputLabelProps={{ shrink: true }}
//               fullWidth
//             />
//           </DialogContent>
//           <DialogActions>
//             <Button onClick={() => setAssignmentEditModalOpen(false)}>Cancel</Button>
//             <Button variant="contained" onClick={handleSaveAssignmentEdit}>Save</Button>
//           </DialogActions>
//         </Dialog>

//         <Dialog open={assignmentDeleteModalOpen} onClose={() => setAssignmentDeleteModalOpen(false)}>
//           <DialogTitle>Confirm Deletion</DialogTitle>
//           <DialogContent>
//             <Typography>
//               Are you sure you want to delete this assignment?
//             </Typography>
//           </DialogContent>
//           <DialogActions>
//             <Button onClick={() => setAssignmentDeleteModalOpen(false)}>Cancel</Button>
//             <Button color="error" variant="contained" onClick={handleConfirmAssignmentDelete}>
//               Delete
//             </Button>
//           </DialogActions>
//         </Dialog>

//         <Modal open={chapterEditModalOpen} onClose={() => setChapterEditModalOpen(false)}>
//           <Box
//             sx={{
//               position: 'absolute',
//               top: '50%',
//               left: '50%',
//               transform: 'translate(-50%, -50%)',
//               width: 500,
//               bgcolor: 'background.paper',
//               borderRadius: 2,
//               boxShadow: 24,
//               p: 4,
//             }}
//           >
//             <Typography variant="h6" mb={2}>Edit Chapter Info</Typography>
//             <TextField
//               fullWidth
//               label="Chapter Title"
//               variant="outlined"
//               value={editedChapterTitle}
//               onChange={(e) => setEditedChapterTitle(e.target.value)}
//               sx={{ mb: 2 }}
//             />
//             <TextField
//               fullWidth
//               multiline
//               rows={4}
//               label="Chapter Description"
//               variant="outlined"
//               value={editedChapterDescription}
//               onChange={(e) => setEditedChapterDescription(e.target.value)}
//               sx={{ mb: 2 }}
//             />
//             <Box display="flex" justifyContent="flex-end" gap={2}>
//               <Button onClick={() => setChapterEditModalOpen(false)}>Cancel</Button>
//               <Button variant="contained" onClick={handleSaveChapterEdit}>Save</Button>
//             </Box>
//           </Box>
//         </Modal>




//       </Container>
//     </Box>
//   );
// }

// export default ChapterDetailsPage;
